<template>
  <form class="user-form" autocomplete="off" @submit.prevent="onSubmitForm">
    <CRow class="align-items-center pb-2" v-if="!hiddenFields.photo">
      <CCol col="auto">
        <div class="user-form__hero">
          <VUserIcon :src="previewPhotoSrc" class="" />
        </div>
      </CCol>
      <CCol col="6">
        <input
          ref="fileInput"
          accept="image/png,image/jpeg,image/jpg"
          multiple=""
          type="file"
          autocomplete="off"
          tabindex="-1"
          style="display: none;"
          @change="onChangeFileInput"
        />
        <CButton class="btn btn-outline-secondary" @click="choosePhoto">
          Choose a profile photo
        </CButton>
      </CCol>
    </CRow>
    <CRow >
      <CCol sm="12">
        <CInput
          v-model="formData.firstName"
          label="First Name"
          placeholder="Enter first name"
        />
      </CCol>
    </CRow>
    <CRow >
      <CCol sm="12">
        <CInput
          v-model="formData.lastName"
          label="Last Name"
          placeholder="Enter last name"
        />
      </CCol>
    </CRow>
    <CRow v-if="!hiddenFields.username">
      <CCol sm="12">
        <CInput
          v-model="formData.username"
          label="Username"
          placeholder="Enter username"
          required
          :disabled="disabledFields.username"
        />
      </CCol>
    </CRow>

    <CRow v-if="!hiddenFields.email">
      <CCol sm="12">
        <CInput
          v-model="formData.email"
          type="email"
          label="User Email"
          placeholder="Enter email"
          required
          :disabled="disabledFields.email"
        />
      </CCol>
    </CRow>
    
    <CRow v-if="formData.hasOwnProperty('phoneNumber') && !hiddenFields.phoneNumber">
      <CCol sm="12">
        <CInput
          v-mask="'###-###-####'"
          v-model="formData.phoneNumber"
          label="Phone Number"
          placeholder="Enter phone number"
        />
      </CCol>
    </CRow>

    <CRow v-if="!hiddenFields.password">
      <CCol sm="12">
        <CInput
          v-model="formData.password"
          label="User Password"
          placeholder="Enter password"
          type="password"
          :disabled="disabledFields.password"
        />
      </CCol>
    </CRow>

    <CRow v-if="formData.hasOwnProperty('role') && !hiddenFields.role">
      <CCol sm="12">
        <CSelect
          label="Role"
          :value="formData.role"
          :options="roleOptions"
          @update:value="changeRole"
          :disabled="disabledFields.role"
        />
      </CCol>
    </CRow>

    <CRow v-if="!hiddenFields.bio">
      <CCol sm="12">
        <CTextarea
          v-model="formData.bio"
          label="User bio"
          placeholder="Enter bio"
          maxlength="100"
        />
      </CCol>
    </CRow>
    <CRow>
      <CCol>
        <div 
          v-show="!user || !user.id"
          class="g-recaptcha" 
          :data-sitekey="recaptcha" 
          data-callback="onRecaptchaSubmit"
          data-expired-callback="onRecaptchaError"
          data-error-callback="onRecaptchaError"
        ></div>
      </CCol>
    </CRow>

    <CRow>
      <CCol col="auto">
        <CButton type="submit" color="outline-primary" :disabled="saving">
          Save
        </CButton>
      </CCol>
    </CRow>

    <p v-if="savingErrorText" class="text-danger mt-3">
      {{ savingErrorText.description || savingErrorText }}
    </p>
  </form>
</template>
<script>
import { authStore, createAuthStoreMixin } from "@/mixins/authStoreMixin";
import { readAsDataURL } from "@/utils/AsyncFileReader";
import { USER_ROLES } from "@/utils/dictionaries/user";

export default {
  name: "VCreateUserForm",

  mixins: [createAuthStoreMixin()],

  props: {
    user: {
      type: Object
    }
  },

  data: () => ({
    saving: false,
    savingErrorText: null,
    previewPhotoSrc: "",
    formData: {
      username: "",
      email: "",
      password: "",
      bio: "",
      role: ""
    },
    hiddenFields: {},
    recaptchaToken: ''
  }),
  

  created() {
    window.onRecaptchaSubmit = this.onRecaptcha
    window.onRecaptchaError = this.resetRecaptcha
  },

  computed: {
    currentUser () {
      return this[authStore.getterTypes.GETTER_USER]
    },
    roleOptions() {
      return [ 
        { value: USER_ROLES.RoleUser, label: 'User' },
        { value: USER_ROLES.RoleAdmin, label: 'Manager' },
        { value: USER_ROLES.RoleSuperAdmin, label: 'Administrator', disabled: this.currentUser.role !== USER_ROLES.RoleSuperAdmin  },
      ]
    },
    disabledFields() {
      return {
        username: this.user?.id ? true : false,
        password: false,
        role: false,
        email: this.user?.id ? true : false
      } || {}
    },
    recaptcha() {
      return `${process.env.VUE_APP_RECAPTCHA_SITE_KEY}`
    }
  },

  watch: {
    user() {
      this.resetRecaptcha()
      this.initData();
    }
  },

  methods: {
    initData() {
      this.hiddenFields = {}
      this.savingErrorText = null;
      if (this.user) {
        this.formData = {
          username: this.user.username,
          email: this.user.email,
          bio: this.user.bio,
          role: this.user.role,
          password: this.user.password,
          firstName: this.user.firstName || this.user.first_name,
          lastName: this.user.lastName || this.user.last_name,
        };

        if (this.user.isManager) {
          this.formData = {
            ...this.formData,
            phoneNumber: this.user.phone_number || this.user.phoneNumber || '',
            role: this.user.role || this.roleOptions[0].value
          }
        }

        if (this.user.hiddenFields) {
          this.hiddenFields = this.user.hiddenFields
        }

        this.previewPhotoSrc = this.user.avatar;
      } else {
        this.previewPhotoSrc = "";
        this.formData = {
          username: "",
          email: "",
          password: "",
          bio: ""
        };
      }
    },

    choosePhoto() {
      this.$refs.fileInput && this.$refs.fileInput.click();
    },

    changeRole(selected) {
      this.formData.role = selected
    },

    async onChangeFileInput(event) {
      try {
        this.previewPhotoSrc = await readAsDataURL(event.target.files[0]);
      } catch (error) {
        this.previewPhotoSrc = "";
      }
    },
    
    resetRecaptcha() {
      grecaptcha.reset()
      this.recaptchaToken = ''
    },
    onRecaptcha(token) {
      this.recaptchaToken = token
    },

    async onSubmitForm() {
      let toUserId = this.user?.id;
      this.savingErrorText = "";
      this.saving = true;
      let formDataPhoto;
      const isNewUser = !toUserId;

      if (this.$refs.fileInput.files[0]) {
        formDataPhoto = new FormData();
        formDataPhoto.append("avatar", this.$refs.fileInput.files[0]);
      }

      const closeModal = (content) => {
        this.$modals.open(content || {
          title: 'Updated successfully',
          body: `User <strong>${ this.user?.username }</strong> was updated.`
        })
        setTimeout(() => {
          this.$emit("success", isNewUser);
        }, 3000)
      }
      let role = this.formData['role'];
      if(role === 'User'){
        this.formData['role'] =  USER_ROLES.RoleUser;
      }

      if (toUserId) {
        try {
          formDataPhoto &&
            (await this[authStore.actionTypes.ACTION_UPLOAD_USER_PHOTO]({
              formData: formDataPhoto,
              toUserId
            }));
          await this[authStore.actionTypes.ACTION_CHANGE_USER_PROFILE]({
            user: this.formData,
            toUserId
          });
          if (this.currentUser && this.currentUser.id === this.user.id) {
            await this[authStore.actionTypes.ACTION_FETCH_USER]()
          }
          closeModal()
          this.initData();
        } catch (error) {
          this.savingErrorText = error?.response?.data;
        } finally {
          this.saving = false;
        }
      } else {
        try {
          const response = await this[authStore.actionTypes.ACTION_ADD_USER]({
            user: this.formData,
            token: this.recaptchaToken
          });
          console.log(" this.formData", this.formData)
          debugger
          toUserId = response.data?.user?.id || response.data.userId;
          formDataPhoto &&
            (await this[authStore.actionTypes.ACTION_UPLOAD_USER_PHOTO]({
              formData: formDataPhoto,
              toUserId
            }));
          
          closeModal({
            title: 'Created successfully',
            body: `User <strong>${ this.formData.username }</strong> was created.`
          })
          this.initData();
        } catch (error) {
          console.dir(error);
          this.savingErrorText = error?.response?.data;
        } finally {
          this.saving = false;
        }
      }
    }
  }
};
</script>
